/**
 * @author Maxime Mustarda <maxime@inarix.com>
 * @file LicenseFilterBtns.tsx
 * @desc Created on Thu May 15 2025 20:59:07
 * @copyright All rights reserved @ Inarix
 */

import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Box, Tooltip } from "@mui/material";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";

import {
  selectAvailPackCount,
  selectExpiringCount,
  selectLowOrEmptyCount,
  selectUnassignedCount,
  filterselectors,
  setFilterModel,
} from "../../redux/slices/licenses/licenseList";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { fetchLicenses } from "../../redux/actions/licenses/licenseListAsyncActions";
import Filters from "./LicenseFilters";
import {
  Colors,
  eyeIconSx,
  filterButtonBoxSx,
  filterButtonBoxHoverSx,
  filterButtonBoxNoHandlerSx,
  titleStyle,
  separatorStyle,
  resolveValueColor,
  resolveValue2Color,
  valueStyle,
} from "./licenseStyles/LicenseFilterButtons";

export { Colors };

const FilterButton: FC<{
  title: string;
  title2?: string;
  value: number;
  value2?: number;
  color: Colors;
  color2?: Colors;
  emptyColor?: Colors;
  emptyColor2?: Colors;
  handler?: () => void;
  filterModel?: any;
  buttonName?: string;
  tooltipText?: string;
  tooltipText2?: string;
}> = ({
  handler,
  title,
  value,
  value2,
  color,
  color2,
  emptyColor,
  emptyColor2,
  filterModel,
  buttonName,
  tooltipText,
  title2,
  tooltipText2,
}) => {
  const capitalize = (s?: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
  const searchValue = filterModel?.items?.find(
    (item) => item.value === buttonName,
  )?.value;

  const manageEyeColor = () => {
    if (searchValue) {
      return (
        <VisibilityIcon
          className="license-eye license-eye-active"
          sx={eyeIconSx}
        />
      );
    } else {
      return (
        <VisibilityOffIcon
          className="license-eye license-eye-inactive"
          sx={eyeIconSx}
        />
      );
    }
  };

  return (
    <Box
      onClick={handler}
      sx={[
        handler ? filterButtonBoxSx : filterButtonBoxNoHandlerSx,
        handler ? { "&:hover": filterButtonBoxHoverSx } : {},
      ]}
    >
      <span style={titleStyle}>
        {title}
        {title2 ? ` / ${capitalize(title2)}` : null}
      </span>
      <Tooltip title={tooltipText} placement="top">
        <span style={valueStyle(resolveValueColor(value, color, emptyColor))}>
          {value}
        </span>
      </Tooltip>
      {title2 && (
        <>
          <span style={separatorStyle}>/</span>
          <Tooltip title={capitalize(tooltipText2)} placement="top">
            <span
              style={valueStyle(
                resolveValue2Color(value2 ?? 0, color, color2, emptyColor2),
              )}
            >
              {value2 ?? 0}
            </span>
          </Tooltip>
        </>
      )}
      {handler ? manageEyeColor() : null}
    </Box>
  );
};
export function LicenseFilterBtns() {
  const [t] = useTranslation("global");
  const unassignedCount = useSelector(selectUnassignedCount);
  const lowOrEmptyCount = useSelector(selectLowOrEmptyCount);
  const expiringCount = useSelector(selectExpiringCount);
  const availPackCount = useSelector(selectAvailPackCount);
  const { filterModel } = useSelector(filterselectors);

  const SetActions = () => {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
    return {
      setFilterModels: (filterModel) => {
        dispatch(setFilterModel(filterModel));
      },
      getLicenses: () => {
        dispatch(fetchLicenses());
      },
    };
  };

  const { setFilterModels, getLicenses } = SetActions();

  const manageFilterModel = (filterName: string) => {
    const existingFilter = filterModel?.items?.find(
      (item) => item.value === filterName,
    );

    if (existingFilter) {
      const newFilterModel = {
        ...filterModel,
        items: filterModel.items.filter((item) => item.value !== filterName),
      };

      setFilterModels(newFilterModel);
    } else {
      const newFilterModel = {
        ...filterModel,
        items: [
          ...(filterModel?.items || []),
          {
            column: t("licenses.licensesStatus"),
            columnType: undefined,
            field: "licensesStatus",
            filter: "LOCAL",
            queryFormat: "",
            service: "special-licenses",
            operator: "$eq",
            realOperator: "$eq",
            value: filterName,
          },
        ],
      };
      setFilterModels(newFilterModel);
    }
  };

  useEffect(() => {
    getLicenses();
  }, [unassignedCount, lowOrEmptyCount, expiringCount, availPackCount]);

  return (
    <div className="license-filter-buttons">
      <Filters />
      <div className="license-filter-buttons-child">
        <FilterButton
          color={Colors.orange}
          emptyColor={Colors.green}
          title={t("licenses.lowOrEmpty")}
          value={lowOrEmptyCount}
          handler={() => manageFilterModel("runningLow")}
          filterModel={filterModel}
          buttonName="runningLow"
          tooltipText={t("licenses.lowOrEmpty_desc")}
        />
        <FilterButton
          color={Colors.red}
          emptyColor={Colors.grey}
          title={t("licenses.expSoon")}
          handler={() => manageFilterModel("expiringSoon")}
          value={expiringCount}
          filterModel={filterModel}
          buttonName="expiringSoon"
          tooltipText={t("licenses.expSoon_desc")}
        />

        <FilterButton
          color={Colors.green}
          emptyColor={Colors.grey}
          title={t("licenses.availPacks")}
          value={availPackCount}
          tooltipText={t("licenses.availablePacks")}
        />
      </div>
    </div>
  );
}
